import { useState } from 'react';
import {
  CheckCheck,
  Copy,
  ExternalLink,
  MapPin,
  Pencil,
  Save,
  TriangleAlert,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  buildWhat3wordsMapUrl,
  formatWhat3wordsAddress,
  normalizeWhat3wordsAddress,
} from '@shared/what3words';
import type { Customer } from '../../types';

interface PipelineWhat3wordsFieldProps {
  customer: Customer;
  isSaving: boolean;
  copiedState: Record<string, boolean>;
  onCopy: (value: string, copyId: string) => void;
  onSave: (value: string | null) => void;
}

export function PipelineWhat3wordsField({
  customer,
  isSaving,
  copiedState,
  onCopy,
  onSave,
}: PipelineWhat3wordsFieldProps) {
  const savedNormalizedValue = normalizeWhat3wordsAddress(customer.what3wordsAddress);
  const savedDisplayValue = formatWhat3wordsAddress(savedNormalizedValue);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(savedDisplayValue ?? '');
  const [error, setError] = useState<string | null>(null);
  const copyId = `what3words-${customer.id}`;
  const isCopied = Boolean(copiedState[copyId]);

  const beginEditing = () => {
    setDraft(savedDisplayValue ?? '');
    setError(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setDraft(savedDisplayValue ?? '');
    setError(null);
    setIsEditing(false);
  };

  const saveAddress = () => {
    const normalized = normalizeWhat3wordsAddress(draft);

    if (draft.trim() && !normalized) {
      setError('Enter ///word.word.word or paste a what3words.com or w3w.co link.');
      return;
    }

    if (normalized === savedNormalizedValue) {
      setDraft(savedDisplayValue ?? '');
      setError(null);
      setIsEditing(false);
      return;
    }

    onSave(normalized);
    setDraft(formatWhat3wordsAddress(normalized) ?? '');
    setError(null);
    setIsEditing(false);
  };

  const openPreciseLocation = () => {
    const url = buildWhat3wordsMapUrl(customer.what3wordsAddress);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-white p-3 text-xs shadow-sm dark:border-slate-700 dark:bg-slate-900/60">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 font-medium text-slate-700 dark:text-slate-200">
            <MapPin className="h-3.5 w-3.5 text-red-500" />
            what3words precise location
          </div>
          <p className="mt-1 text-[11px] leading-4 text-slate-500 dark:text-slate-400">
            Additional to the normal installation address.
          </p>
        </div>
        {!savedDisplayValue && !isEditing && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-[10px] font-medium text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
            <TriangleAlert className="h-3 w-3" />
            Missing
          </span>
        )}
      </div>

      {isEditing ? (
        <form
          className="mt-3 space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            saveAddress();
          }}
        >
          <Input
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              if (error) setError(null);
            }}
            placeholder="///word.word.word or paste a link"
            autoComplete="off"
            aria-label="what3words precise location"
            aria-invalid={Boolean(error)}
            className="h-9 font-mono text-xs"
            autoFocus
          />
          {error ? (
            <p className="text-[11px] leading-4 text-red-600 dark:text-red-400" role="alert">
              {error}
            </p>
          ) : (
            <p className="text-[11px] leading-4 text-slate-500 dark:text-slate-400">
              Links and leading /// are cleaned automatically. This does not verify the location
              with the paid API.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={cancelEditing}
              disabled={isSaving}
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </Button>
            <Button type="submit" size="sm" className="h-8 px-3 text-xs" disabled={isSaving}>
              <Save className="h-3.5 w-3.5" />
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      ) : savedDisplayValue ? (
        <div className="mt-3">
          <code className="block rounded bg-slate-100 px-2.5 py-2 font-mono text-sm font-medium text-slate-800 dark:bg-slate-800 dark:text-slate-100">
            {savedDisplayValue}
          </code>
          <div className="mt-2 grid grid-cols-3 gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={() => onCopy(savedDisplayValue, copyId)}
            >
              {isCopied ? (
                <CheckCheck className="h-3.5 w-3.5 text-green-600" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
              {isCopied ? 'Copied' : 'Copy'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={openPreciseLocation}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={beginEditing}
              disabled={isSaving}
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-3 rounded-md bg-amber-50 p-2.5 dark:bg-amber-950/20">
          <p className="text-[11px] leading-4 text-amber-800 dark:text-amber-300">
            No precise location has been added for this customer.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2 h-8 border-amber-300 bg-white px-3 text-xs text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:bg-slate-900 dark:text-amber-200"
            onClick={beginEditing}
          >
            <MapPin className="h-3.5 w-3.5" />
            Add precise location
          </Button>
        </div>
      )}
    </div>
  );
}
